/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Fragment, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Paper, Grid, Box } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import PowerIconSvg from "../assets/images/power_icon.svg";
import GasIconSvg from "../assets/images/gas_icon.svg";
import GoIconSvg from "../assets/images/go_icon.svg";
import { Button } from "../components/core/Button";
import { FamilyPicture } from "../components/core/FamilyPicture";
import { useTypedSelector, RootDispatch, ConfigurationActions, AuthActions } from "../redux";
import { Colors } from "../styles";
import { useStyles } from "../useStyles";
import { useDispatch } from "react-redux";
import {
    PostalAddress,
    CadasterAddress,
    unwrap,
    OperationState,
} from "../clients/FirebaseClient/types";

const useFormStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
    },
    containerImage: {},
    containerForm: {
        flex: 1,
        marginLeft: 24,
        marginRight: 24,
    },
    containerFormContent: {
        display: "flex",
        flexDirection: "column",
    },
    boxPaper: {
        elevation: 3,
        borderRadius: 8,
        marginBottom: 32,
    },
    boxContainer: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        backgroundColor: Colors.anthracite5,
    },
    boxImageContainer: {
        marginTop: 16,
        marginBottom: 16,
        marginLeft: 16,
    },
    boxImage: {
        width: 64,
        height: 64,
    },
    boxContent: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        marginLeft: 32,
        verticalAlign: "middle",
        alignSelf: "center",
        marginTop: 16,
        marginBottom: 16,
    },
    boxSorry: {
        color: Colors.mellowYellow,
        fontFamily: "innogyWEB",
        fontSize: 22,
        fontStretch: "normal",
        fontStyle: "normal",
        letterSpacing: "normal",
        lineHeight: 1.4,
    },
    boxResponsible: {
        color: Colors.anthracite50,
        fontFamily: "innogyWEB",
        fontSize: 18,
        fontStretch: "normal",
        fontStyle: "normal",
        letterSpacing: "normal",
        lineHeight: 1.4,
    },
    boxResponsibleBold: {
        color: Colors.anthracite,
        fontFamily: "innogyWEB",
        fontSize: 18,
        fontStretch: "normal",
        fontStyle: "bold",
        letterSpacing: "normal",
        lineHeight: 1.4,
    },
    configureButtonContainer: {
        marginLeft: "auto",
    },
    backbuttonContainer: {
        marginTop: 24,
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
    },
    backbuttonImageContainer: {
        display: "flex",
        verticalAlign: "middle",
        alignSelf: "center",
    },
    backbuttonContent: {
        marginLeft: 16,
        flex: 1,
        display: "flex",
        flexDirection: "column",
        verticalAlign: "middle",
        alignSelf: "center",
        fontFamily: "innogyWEB",
        fontSize: 22,
        fontStretch: "normal",
        fontStyle: "normal",
        letterSpacing: "normal",
        lineHeight: 1.4,
    },
    topHr: {
        marginTop: -5,
    },
    bottomHr: {
        marginTop: 24,
    },
    link: {
        color: "inherit",
        textDecoration: "inherit",
    },
});

const formatedAddressline = (
    s: string | null,
    h: string | null,
    z: string | null,
    c: string | null,
): string => {
    const sh = s && h ? s + " " + h : s || h; // Strasse und Hausnummer mit Blank dazwischen wenn beide gegeben
    const zc = z && c ? z + " " + c : z || c; // Plz und Ort mit Blank dazwischen, wenn beide gegeben
    return (sh && zc ? sh + ", " + zc : sh || zc) + ""; // Strasse Hausnummer "komma Blank" plz ort, wenn nötig
};

const formatedMarkline = (m: string | null, c: string | null, p: string | null): string => {
    return m && c ? m + ", " + c + ", " + p : m + ", " + p; // m=Mark, c=Coridor, p=Parcel
};

export const useCreateOrLoginCallback = (
    uid: string | undefined,
    address: PostalAddress | CadasterAddress,
) => {
    const dispatch: RootDispatch = useDispatch();
    const navigate = useNavigate();
    return useCallback(async () => {
        if (!uid) {
            dispatch(AuthActions.signInWithMSP());
            return;
        }
        const operationState = await dispatch(
            ConfigurationActions.initializeOperation({ buildingAddress: unwrap(address) }),
        );
        if (operationState.status === OperationState.SUCCEEDED) {
            console.log("result:", operationState.result);
            // dispatch(ConfigurationActions.load(operationState.result || "strange operation error"));
            navigate(`/configuration/${operationState.result}`);
        } else {
            console.error(operationState);
        }
    }, [address, dispatch, uid, navigate]);
};

export const AvailableProducts = (): JSX.Element => {
    const classes = useStyles();
    const formClasses = useFormStyles();

    const uid = useTypedSelector((state) => state.auth.uid);
    const address = useTypedSelector(
        (state) =>
            state.configuration.configurations[state.configuration.currentConfigurationId!].state
                .buildingAddress!.state,
    );
    const powerPartner = useTypedSelector((state) => state.configuration.productPartners?.power);
    const gasPartner = useTypedSelector((state) => state.configuration.productPartners?.gas);

    const createOrLogin = useCreateOrLoginCallback(uid, address);

    return (
        <Grid container className={formClasses.container} data-testid="availableproducts-testId">
            <Box sx={{ display: { xs: "none", md: "block" } }}>
                <div className={formClasses.containerImage}>
                    <FamilyPicture />
                </div>
            </Box>
            <div className={formClasses.containerForm}>
                <div className={formClasses.containerFormContent}>
                    <div className={formClasses.topHr}>
                        <hr className={classes.hr} />
                    </div>

                    <h1 className={classes.h2}>Ihr Netzanschluss</h1>
                    <p className={classes.p}>
                        Wir bieten Ihnen die Möglichkeit, Ihren Strom- und/oder Gasanschluss einfach
                        und schnell online zu beantragen. Eine Änderung, Demontage, oder die
                        Anmeldung einer Ladeeinrichtung für Elektrofahrzeuge ist ebenfalls möglich.
                    </p>
                    <h3 className={classes.h3}>Meine Bauadresse</h3>
                    <p className={classes.p}>
                        {formatedAddressline(
                            address.street?.value || "",
                            address.houseNumber?.value || "",
                            address.zipCode.value,
                            address.city.value,
                        )}
                        {address.type === "cadaster" && (
                            <div>
                                {formatedMarkline(
                                    address.mark?.value || "",
                                    address.corridor?.value || "",
                                    address.parcel?.value || "",
                                )}
                            </div>
                        )}
                    </p>
                    {address.type !== "cadaster" && (
                        <Fragment>
                            <h3 className={classes.h3}>
                                Verfügbare Anschlussarten im Syna Netzgebiet
                            </h3>

                            <Paper className={formClasses.boxPaper} elevation={3}>
                                <div className={formClasses.boxContainer}>
                                    <div className={formClasses.boxImageContainer}>
                                        <img
                                            alt=""
                                            src={PowerIconSvg}
                                            className={formClasses.boxImage}
                                        />
                                    </div>
                                    <div className={formClasses.boxContent}>
                                        {powerPartner ? (
                                            powerPartner.foreignProvider === true ? (
                                                <Fragment>
                                                    <p className={formClasses.boxSorry}>
                                                        Leider sind wir in diesem Gebiet nicht Ihr
                                                        Strom Netzbetreiber.
                                                    </p>
                                                    <span className={formClasses.boxResponsible}>
                                                        Für Strom ist bei Ihnen zuständig:{" "}
                                                        <span
                                                            className={
                                                                formClasses.boxResponsibleBold
                                                            }
                                                        >
                                                            {powerPartner.companyName}
                                                        </span>
                                                    </span>
                                                    <a
                                                        href={powerPartner.companyWeb}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={formClasses.boxResponsible}
                                                    >
                                                        {powerPartner.companyWeb}
                                                    </a>
                                                </Fragment>
                                            ) : (
                                                <p className={classes.p}>
                                                    Syna ist Ihr zuständiger Strom Netzbetreiber.
                                                </p>
                                            )
                                        ) : (
                                            <p className={classes.p}>
                                                Es konnte kein zuständiger Strom Netzbetreiber
                                                ermittelt werden.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </Paper>

                            <Paper className={formClasses.boxPaper} elevation={3}>
                                <div className={formClasses.boxContainer}>
                                    <div className={formClasses.boxImageContainer}>
                                        <img
                                            alt=""
                                            src={GasIconSvg}
                                            className={formClasses.boxImage}
                                        />
                                    </div>
                                    <div className={formClasses.boxContent}>
                                        {gasPartner ? (
                                            gasPartner.foreignProvider === true ? (
                                                <Fragment>
                                                    <p className={formClasses.boxSorry}>
                                                        Leider sind wir in diesem Gebiet nicht Ihr
                                                        Gas Netzbetreiber.
                                                    </p>
                                                    <span className={formClasses.boxResponsible}>
                                                        Für Gas ist bei Ihnen zuständig:{" "}
                                                        <span
                                                            className={
                                                                formClasses.boxResponsibleBold
                                                            }
                                                        >
                                                            {gasPartner.companyName}
                                                        </span>
                                                    </span>
                                                    <a
                                                        href={gasPartner.companyWeb}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={formClasses.boxResponsible}
                                                    >
                                                        {gasPartner.companyWeb}
                                                    </a>
                                                </Fragment>
                                            ) : (
                                                <p className={classes.p}>
                                                    Syna ist Ihr zuständiger Gas Netzbetreiber.
                                                </p>
                                            )
                                        ) : (
                                            <p className={classes.p}>
                                                Es konnte kein zuständiger Gas Netzbetreiber
                                                ermittelt werden.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </Paper>
                        </Fragment>
                    )}
                    {(powerPartner?.foreignProvider === false ||
                        gasPartner?.foreignProvider === false) && (
                        <div className={formClasses.configureButtonContainer}>
                            <Button mode="primary" onClick={createOrLogin}>
                                Antrag erstellen
                            </Button>
                        </div>
                    )}
                    <div className={formClasses.bottomHr}>
                        <hr className={classes.hr} />
                    </div>
                    <Link to="/address" className={formClasses.link}>
                        <div className={formClasses.backbuttonContainer}>
                            <div className={formClasses.backbuttonImageContainer}>
                                <img alt="" src={GoIconSvg} />
                            </div>
                            <div className={formClasses.backbuttonContent}>
                                Neue Bauadresse prüfen
                            </div>
                        </div>
                    </Link>
                </div>
            </div>
        </Grid>
    );
};
